/**
 * 배포 화면 히스토리의 계산부 — 사내/현장을 나누고 서비스별로 거른다.
 * 그리는 쪽(DeploySection)과 분리해 테스트한다.
 */
import { SITE_BATCH_SERVICE_CATALOG } from '../../../models/SiteBatch';
import type { RecentDeploymentItem } from '../../../views/SiteDashboardView/hooks/useRecentDeployments';
import type { DeployTabType } from '../../../views/SiteDashboardView/types';

/**
 * 이 배포가 간 곳. 현장 배포(site-deploy.yaml)만 environment 'remote' 로 남는다 —
 * 'company' 와 예전 단건 배포의 'production' 은 둘 다 사내 서버로 간 것이다.
 */
export function deployScope(item: Pick<RecentDeploymentItem, 'environment'>): DeployTabType {
  return item.environment === 'remote' ? 'remote' : 'company';
}

/** 한 곳(scope)의 배포만, 서비스(app)를 고르면 그 서비스만. app 이 null 이면 전체. */
export function filterHistory(
  items: readonly RecentDeploymentItem[],
  scope: DeployTabType,
  app: string | null,
): RecentDeploymentItem[] {
  return items.filter((i) => deployScope(i) === scope && (app === null || i.appname === app));
}

/** 서비스 고르기 칩 — 그곳에 배포 기록이 있는 서비스만, 배포 순서대로. */
export function historyApps(
  items: readonly RecentDeploymentItem[],
  scope: DeployTabType,
): Array<{ appname: string; label: string; count: number }> {
  const counts = new Map<string, number>();
  for (const i of items) {
    if (deployScope(i) !== scope || !i.appname) continue;
    counts.set(i.appname, (counts.get(i.appname) ?? 0) + 1);
  }
  return SITE_BATCH_SERVICE_CATALOG.filter((s) => counts.has(s.appname)).map((s) => ({
    appname: s.appname,
    label: s.label,
    count: counts.get(s.appname) ?? 0,
  }));
}

/** 그곳의 가장 최근 배포 — 목록은 deployed_at 내림차순으로 온다. */
export function latestDeploy(
  items: readonly RecentDeploymentItem[],
  scope: DeployTabType,
): RecentDeploymentItem | null {
  return items.find((i) => deployScope(i) === scope) ?? null;
}
